import { useEffect, type RefObject } from 'react';
import type { ModelViewerElement } from '@google/model-viewer';
import { applyDynamicModelViewerZoomLimits } from '@/utils/modelViewerZoom';

function modelHasMeasurableDimensions(el: ModelViewerElement): boolean {
  try {
    const { x, y, z } = el.getDimensions();
    return x > 0 || y > 0 || z > 0;
  } catch {
    return false;
  }
}

/** Wire `load` + dimension checks so zoom limits track the loaded mesh bounding box. */
function bindDynamicZoomLimits(el: ModelViewerElement): () => void {
  const onLoad = () => {
    applyDynamicModelViewerZoomLimits(el);
  };

  el.addEventListener('load', onLoad);

  // Blob / cached models may already be ready when the effect runs.
  if (modelHasMeasurableDimensions(el)) {
    onLoad();
  }

  return () => {
    el.removeEventListener('load', onLoad);
  };
}

/**
 * Recomputes min/max orbit radius and field-of-view when the GLB loads or `src` changes.
 *
 * Zoom limits scale with `model-viewer.getDimensions()` so small parts and large halls
 * both get usable pinch / scroll zoom without changing per-product saved camera poses.
 *
 * Pass `null` for `modelSrc` when the viewer is unmounted (e.g. modal closed) so listeners
 * re-bind on reopen.
 */
export function useModelViewerDynamicZoom(
  modelViewerRef: RefObject<ModelViewerElement | null>,
  modelSrc?: string | null
): void {
  useEffect(() => {
    if (!modelSrc) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return;
      const el = modelViewerRef.current;
      if (!el) return;
      cleanup = bindDynamicZoomLimits(el);
    };

    attach();

    let frameId = 0;
    if (!cleanup) {
      // Modal / conditional mounts may attach the ref one frame after `modelSrc` is set.
      frameId = requestAnimationFrame(attach);
    }

    return () => {
      cancelled = true;
      if (frameId) cancelAnimationFrame(frameId);
      cleanup?.();
    };
  }, [modelViewerRef, modelSrc]);
}
